import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './BarRestaurantOrderCard.css';
import AppIcon from './icons/AppIcon';

export default function BarRestaurantOrderCard({ card, order: orderProp, onOpenCard, isDragging }) {
  const [order, setOrder] = useState(orderProp || null);
  const [loading, setLoading] = useState(!orderProp);
  const [timeElapsed, setTimeElapsed] = useState('0m');

  // Função para calcular tempo decorrido
  const calculateTimeElapsed = (timestamp) => {
    if (!timestamp) return '0m';
    
    const agora = new Date();
    const entrada = new Date(timestamp);
    const diffMs = agora - entrada;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d ${diffHours % 24}h`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMins % 60}m`;
    } else if (diffMins > 0) {
      return `${diffMins}m`;
    } else {
      return `${diffSecs}s`;
    }
  };

  // Carregar dados do pedido
  useEffect(() => {
    if (orderProp) {
      setOrder(orderProp);
      setLoading(false);
      return;
    }

    if (card?.entity_type === 'bar_restaurant_order' && card?.entity_id) {
      loadOrderData();
    }
  }, [card?.entity_id, orderProp]);

  // Atualizar cronômetro a cada segundo
  useEffect(() => {
    if (!order?.timestamp_entrada_lista) return;

    setTimeElapsed(calculateTimeElapsed(order.timestamp_entrada_lista));
    const interval = setInterval(() => {
      setTimeElapsed(calculateTimeElapsed(order.timestamp_entrada_lista));
    }, 1000);

    return () => clearInterval(interval);
  }, [order?.timestamp_entrada_lista]);

  const loadOrderData = async () => {
    try {
      setLoading(true);

      // Buscar dados do pedido
      const { data: orderData, error: orderError } = await supabase
        .from('bar_restaurant_orders')
        .select('*')
        .eq('id', card.entity_id)
        .single();

      if (orderError) throw orderError;
      setOrder(orderData);
    } catch (error) {
      console.error('Erro ao carregar dados do pedido:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    const statusMap = {
      'confirmado': 'confirmed',
      'em_producao': 'in_production',
      'pronto': 'ready',
      'entregue': 'delivered',
      'cancelado': 'cancelled'
    };
    return statusMap[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'pedidos_em_aberto': 'Pedidos em aberto',
      'em_producao': '⏳ Em Produção',
      'pronto': '✓ Pronto',
      'entregue': '✓ Entregue',
      'cancelado': '✗ Cancelado'
    };
    return statusMap[status] || status;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR');
  };

  const getElapsedTimeClass = (elapsed) => {
    if (!elapsed) return 'time-green';
    
    let totalMinutes = 0;
    
    // Extrair dias (d)
    const daysMatch = elapsed.match(/(\d+)d/);
    if (daysMatch) {
      totalMinutes += parseInt(daysMatch[1]) * 24 * 60;
    }
    
    // Extrair horas (h)
    const hoursMatch = elapsed.match(/(\d+)h/);
    if (hoursMatch) {
      totalMinutes += parseInt(hoursMatch[1]) * 60;
    }
    
    // Extrair minutos (m)
    const minutesMatch = elapsed.match(/(\d+)m/);
    if (minutesMatch) {
      totalMinutes += parseInt(minutesMatch[1]);
    }
    
    // Extrair segundos (s)
    const secondsMatch = elapsed.match(/(\d+)s/);
    if (secondsMatch) {
      totalMinutes += parseInt(secondsMatch[1]) / 60;
    }

    if (totalMinutes <= 10) return 'time-green';
    if (totalMinutes <= 20) return 'time-yellow';
    return 'time-red';
  };

  if (loading || !order) {
    return (
      <div className="bar-restaurant-order-card loading" style={{ opacity: isDragging ? 0.5 : 1 }}>
        <div className="order-loading">
          <AppIcon name="progress_activity" className="spinning" />
        </div>
      </div>
    );
  }

  const customFields = card?.custom_fields || order?.custom_fields || {};
  const orderPayload = order?.order_payload || {};
  const statusAtual = order?.status_atual || customFields.status_atual || 'pendente';
  const itensDetalhados = orderPayload?.itens || customFields.itens_detalhados || [];
  const timestampEntrada = order?.timestamp_entrada_lista || customFields.timestamp_entrada_lista || order?.created_at || card?.created_at;

  return (
    <div 
      className="bar-restaurant-order-card" 
      onClick={() => onOpenCard && onOpenCard(card || order)}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {/* Header com Mesa e Total */}
      <div className="order-header">
        <div className="order-mesa">
          <AppIcon name="table_restaurant" />
          <span className="mesa-number">Mesa {orderPayload.mesa || customFields.mesa || '?'}</span>
        </div>
        <div className="order-total">
          <span className="total-value">R$ {(orderPayload.total || customFields.total || 0).toFixed(2)}</span>
        </div>
      </div>

      {/* Lista de Itens */}
      {itensDetalhados.length > 0 && (
        <div className="order-items">
          {itensDetalhados.map((item, idx) => (
            <div key={idx} className="order-item">
              <div className="item-name">
                <span className="item-qty">x{item.quantidade}</span>
                <span className="item-title">{item.nome}</span>
              </div>
              <div className="item-price">
                R$ {item.preco_total?.toFixed(2) || '0.00'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer com Status e Timestamp */}
      <div className="order-footer">
        <div className={`order-status ${getStatusBadgeColor(statusAtual)}`}>
          {getStatusLabel(statusAtual)}
        </div>
        <div className="order-time-group">
          <div className="order-time">
            <AppIcon name="schedule" />
            <span>{formatTime(timestampEntrada)}</span>
          </div>
          <div className={`order-elapsed-time ${getElapsedTimeClass(timeElapsed)}`}>
            <AppIcon name="timer" />
            <span>{timeElapsed}</span>
          </div>
        </div>
      </div>

      {/* Indicador de quantidade de itens */}
      {itensDetalhados.length > 0 && (
        <div className="order-item-count">
          {itensDetalhados.length} item{itensDetalhados.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
